<template>
	<div class="stage">
		<page-header title="Devices" />

		<filter-bar
			:show-filters="showFilters"
			:has-export="display === 'devices'"
			:show-add-button="$global.hasUserRole(userRoles, 'devices:create')"
			:add-button-label="display === 'devices' ? 'Add Device' : 'Add Template'"
			@toggle-filters="showFilters = !showFilters"
			@reset-filters="resetSearchParams"
			@export="exportToCsv"
			@add-new="display === 'devices' ? routeTo('/devices/new') : routeTo('/devicetemplates/new')"
		/>

		<filter-section
			:show="showFilters"
			:filters="searchParams"
			:search-term="searchField"
			:tenants="tenants"
			date-label="Date Added"
			show-tenant-filter
			show-date-filter
			show-search-filter
			search-label="Name"
			@update:filters="searchParams = $event"
			@update:search-term="searchField = $event"
			@tenant-change="$global.setSelectedTenant"
		/>

		<div
			v-if="$global.hasUserRole(userRoles, 'devicetemplates:create')"
			class="mt-4 flex items-center justify-center pb-4 md:pb-0"
		>
			<div class="relative flex w-full flex-wrap gap-2 self-center rounded-lg p-0.5 md:w-[800px] md:flex-nowrap">
				<button
					v-for="option in displayOptions"
					:key="option.value"
					type="button"
					class="relative w-[calc(50%-0.25rem)] cursor-default whitespace-nowrap rounded-full border border-main px-4 py-2 text-sm font-medium transition-all active:scale-[0.98] dark:border-main md:w-1/2"
					:class="{
						'border-transparent bg-main text-white dark:border-transparent dark:bg-accentMain dark:text-black':
							display === option.value,
						'bg-transparent text-black hover:bg-main/10 dark:text-light dark:hover:bg-offwhite/10':
							display !== option.value
					}"
					@click="setDisplay(option.value)"
				>
					<span class="hidden sm:block">{{ option.label }}</span>
					<span class="block sm:hidden">{{ option.shortLabel || option.label }}</span>
				</button>
			</div>
		</div>

		<div v-if="display === 'devices'">
			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Devices'"
				:total-items="totalDevices"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="pages"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="devices"
						:columns="columns"
						item-type="devices"
						base-route="devices"
						:can-edit="$global.hasUserRole(userRoles, 'devices:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-mobile',
							title: 'No devices yet',
							description: 'Add your first device to start provisioning hardware.',
							action: $global.hasUserRole(userRoles, 'devices:create')
								? {
										label: 'Add Device',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/devices/new')
									}
								: null
						}"
						:actions="[
							{
								handler: (device) => routeTo(`/devices/${device.id}`),
								icon: 'fa-light fa-pencil',
								label: 'Edit'
							},
							{
								permission: 'devices:delete',
								handler: deleteDevice,
								icon: 'fa-light fa-delete-left',
								label: 'Delete'
							}
						]"
					>
						<template #tenant="{ item }">
							{{ item.tenant?.companyName || 'Unassigned' }}
						</template>
						<template #template="{ item }">
							{{ item.template?.name || 'None' }}
						</template>
						<template #enabled="{ item }">
							<span :class="item.enabled ? 'text-green-600' : 'text-red-600'">
								{{ item.enabled ? 'Enabled' : 'Disabled' }}
							</span>
						</template>
						<template #last_timestamp="{ item }">
							{{
								item.last_timestamp
									? $global.dateFormat(item.last_timestamp, 'DD/MM/YY HH:mm')
									: 'Never'
							}}
						</template>
						<template #mobile-view="{ item }">
							<div class="flex w-full flex-col">
								<div class="text-md absolute right-4 top-4 block max-w-[160px]">
									<p class="text-right font-semibold uppercase text-gray-400">Tenant</p>
									<p class="truncate text-right">{{ item.tenant?.companyName || 'Unassigned' }}</p>
								</div>
								<div class="flex flex-col gap-2">
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">MAC Address:</p>
										<p>{{ item.mac }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Serial Number:</p>
										<p>{{ item.serial_number || 'N/A' }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Template:</p>
										<p>{{ item.template?.name || 'None' }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Status:</p>
										<p>
											<span :class="item.enabled ? 'text-green-600' : 'text-red-600'">
												{{ item.enabled ? 'Enabled' : 'Disabled' }}
											</span>
										</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Date Added:</p>
										<p>{{ $global.dateFormat(item.createdAt, 'DD/MM/YY') }}</p>
									</div>
								</div>
							</div>
						</template>
					</generic-table>
				</template>
			</data-view>
		</div>

		<div v-if="display === 'templates'">
			<div class="flex items-center justify-between">
				<div class="flex items-center">
					<p class="textUppercase textSml whitespace-nowrap">
						Provisioning Templates
						<span class="fontHeavy textGreen textSml"> {{ deviceTemplates.length }}</span>
					</p>
				</div>
			</div>

			<div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
				<div v-if="deviceTemplates.length === 0" class="col-span-full py-8 text-center">
					<div
						class="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border border-dashed border-light-600 p-6 dark:border-offmain"
					>
						<span class="fa-light fa-file-template mb-4 text-4xl text-light-700 dark:text-light-600"></span>
						<h3 class="mb-2 text-lg font-medium text-main dark:text-white">No templates found</h3>
						<p class="mb-4 text-sm text-mainLight dark:text-light-600">
							You haven't created any provisioning templates yet.
						</p>
						<button
							v-if="$global.hasUserRole(userRoles, 'devicetemplates:create')"
							class="jmiBtn blue"
							@click="routeTo('/devicetemplates/new')"
						>
							Create Template
						</button>
					</div>
				</div>
				<div
					v-for="template in deviceTemplates"
					:key="template.id"
					class="cursor-pointer rounded-lg border border-light-600 bg-white p-5 shadow-sm transition-all hover:border-blue-500 hover:shadow-md dark:border-offmain dark:bg-offmaindark"
					@click="editTemplate(template)"
				>
					<div class="flex h-full flex-col gap-3">
						<div class="flex items-center justify-between">
							<h3 class="truncate text-lg font-medium">{{ template.name }}</h3>
							<div class="flex gap-1">
								<button
									v-if="$global.hasUserRole(userRoles, 'devices:update')"
									class="rounded p-1 text-light-700 hover:bg-light-100 hover:text-blue-500 dark:text-light-600 dark:hover:bg-offmain"
									@click.stop="editTemplate(template)"
								>
									<span class="fa-light fa-pencil"></span>
								</button>
								<button
									v-if="$global.hasUserRole(userRoles, 'devices:delete')"
									class="rounded p-1 text-light-700 hover:bg-light-100 hover:text-red-500 dark:text-light-600 dark:hover:bg-offmain"
									@click.stop="deleteTemplate(template)"
								>
									<span class="fa-light fa-delete-left"></span>
								</button>
							</div>
						</div>
						<p class="line-clamp-3 flex-grow text-sm text-main dark:text-light-600">
							{{ template.description || 'No description available' }}
						</p>
						<div class="mt-auto flex items-center justify-between">
							<span class="text-xs text-mainLight dark:text-light-700">
								{{ $global.dateFormat(template.createdAt) }}
							</span>
							<span
								v-if="template.manufacturer || template.model"
								class="text-xs text-mainLight dark:text-light-700"
							>
								{{ [template.manufacturer, template.model].filter(Boolean).join(' ') }}
							</span>
						</div>
						<div class="mt-2 flex items-center">
							<span class="text-xs text-mainLight dark:text-light-700">
								<span class="font-medium">Tenant:</span>
								{{ template.tenant?.companyName || 'Unassigned' }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<Dialog :open="showTemplateEditor" @update:open="showTemplateEditor = $event">
		<DialogContent
			class="max-h-[95vh] w-[95vw] max-w-full overflow-hidden rounded-xl p-0"
			:disable-overlay-close="true"
			@close="showTemplateEditor = false"
		>
			<div class="flex h-[95vh] flex-col rounded-xl">
				<DialogHeader class="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
					<div>
						<DialogTitle>{{ editingTemplate?.id ? 'Edit' : 'New' }} Provisioning Template</DialogTitle>
						<DialogDescription>
							{{
								editingTemplate?.id
									? 'Edit the template configuration'
									: 'Create a new provisioning template'
							}}
						</DialogDescription>
					</div>
				</DialogHeader>

				<div v-if="loading" class="flex h-full items-center justify-center">
					<div class="text-center">
						<div class="spinner mb-4"></div>
						<p>Loading template...</p>
					</div>
				</div>

				<div v-else class="flex h-full flex-col overflow-hidden">
					<div class="border-b border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
						<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div class="space-y-4">
								<FormField label="Name" class="w-full">
									<input
										v-model="editingTemplate.name"
										type="text"
										class="form-control w-full"
										placeholder="Template Name"
										required
									/>
								</FormField>

								<FormField label="Description" class="w-full">
									<input
										v-model="editingTemplate.description"
										type="text"
										class="form-control w-full"
										placeholder="Template Description"
									/>
								</FormField>
							</div>

							<div class="space-y-4">
								<FormField label="Tenant" class="w-full" has-icon icon-class="fa-chevron-down">
									<div class="relative">
										<select
											v-model="editingTemplate.tenantId"
											class="form-control w-full appearance-none pr-10"
											required
										>
											<option value="" disabled selected>-- Select a Tenant --</option>
											<option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
												{{ tenant.companyName }}
											</option>
										</select>
									</div>
								</FormField>

								<div class="grid grid-cols-2 gap-4">
									<FormField label="Manufacturer" class="w-full">
										<input
											v-model="editingTemplate.manufacturer"
											type="text"
											class="form-control w-full"
											placeholder="Manufacturer"
										/>
									</FormField>

									<FormField label="Model" class="w-full">
										<input
											v-model="editingTemplate.model"
											type="text"
											class="form-control w-full"
											placeholder="Model"
										/>
									</FormField>
								</div>
							</div>
						</div>
					</div>

					<div class="flex-grow overflow-hidden p-4">
						<FormField label="Template Content" class="h-full">
							<div class="h-[calc(95vh-220px)] w-full">
								<v-ace-editor
									ref="modalEditor"
									v-model:value="editingTemplate.template"
									:theme="isDarkMode ? 'github_dark' : 'chrome'"
									style="height: 100%; width: 100%"
									:options="{
										fontSize: '14px',
										showPrintMargin: false,
										showGutter: true,
										highlightActiveLine: true,
										showLineNumbers: true,
										tabSize: 2,
										wrap: true
									}"
								/>
							</div>
						</FormField>
					</div>

					<div
						class="flex justify-end space-x-4 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
					>
						<button class="jmiBtn red h-10 min-w-[100px]" @click="showTemplateEditor = false">
							Cancel
						</button>
						<button class="jmiBtn blue h-10 min-w-[100px]" @click="saveTemplate">Save</button>
					</div>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<script>
import devicesApi from '../scripts/devices/devices.api'
import deviceTemplatesApi from '../scripts/devicetemplates/devicetemplates.api'
import { router } from '../router'
import moment from 'moment'
import axios from 'axios'
import debounce from 'lodash.debounce'
import { VAceEditor } from 'vue3-ace-editor'
import 'ace-builds/src-noconflict/mode-xml'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-chrome'
import 'ace-builds/src-noconflict/theme-github_dark'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import FormField from '../components/common/FormField.vue'

export default {
	components: {
		VAceEditor,
		Dialog,
		DialogContent,
		DialogHeader,
		DialogTitle,
		DialogDescription,
		FormField
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'mac',
					label: 'MAC Address',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-2'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'template',
					label: 'Template',
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'serial_number',
					label: 'Serial Number',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'enabled',
					label: 'Status',
					class: 'col-xs-12 col-sm-2 col-md-1 col-lg-1'
				},
				{
					key: 'last_timestamp',
					label: 'Last Connection',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 hideTabletLandscape'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-1 hideTablet'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			},
			devices: [],
			deviceTemplates: [],
			totalDevices: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'mac',
			reverse: false,
			loading: false,
			searchField: '',
			display: 'devices',
			displayOptions: [
				{ value: 'devices', label: 'Devices', shortLabel: 'Devices' },
				{ value: 'templates', label: 'Provisioning Templates', shortLabel: 'Templates' }
			],
			showTemplateEditor: false,
			editingTemplate: {
				name: '',
				manufacturer: '',
				model: '',
				description: '',
				template: '',
				tenantId: ''
			}
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalDevices / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		},
		isDarkMode() {
			return document.documentElement.classList.contains('dark')
		}
	},

	watch: {
		dates: {
			handler(val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					this.reloadDevices()
				}
			},
			deep: true
		},

		searchParams: {
			handler() {
				if (this.display === 'devices') {
					this.reloadDevices()
				}

				if (this.display === 'templates') {
					this.loadTemplates()
				}
			},
			deep: true
		},

		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},

		'$route.query': {
			handler(newQuery) {
				const tab = newQuery.tab
				if (tab && ['devices', 'templates'].includes(tab) && tab !== this.display) {
					this.display = tab
				}
			},
			deep: true,
			immediate: true
		},

		display(newValue) {
			if (newValue === 'templates') {
				this.loadTemplates()
			}
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
		}

		this.debouncedWatch = debounce(this.reloadDevices, 1000)

		if (this.$route.query?.globalSearch) {
			this.searchField = this.$route.query.globalSearch
		}

		this.reloadDevices()
		this.loadTemplates()
	},

	mounted() {
		const tab = this.$route.query.tab
		if (tab && ['devices', 'templates'].includes(tab)) {
			this.display = tab
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
	},

	methods: {
		setDisplay(tab) {
			this.display = tab
			this.$router.push({ query: { ...this.$route.query, tab } })
		},

		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadDevices()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalDevices / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.$nextTick(() => {
				this.reloadDevices()
			})
		},

		changePage(page) {
			this.currentPage = page
			this.reloadDevices()
		},

		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
			this.dates = []
			this.searchField = ''
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			if (this.searchParams.selectedTenant) {
				params.where.selectedTenant = this.searchParams.selectedTenant
			}

			if (this.searchField) {
				params.searchTerm = this.searchField
			}

			return params
		},

		async reloadDevices(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await devicesApi.getAll(params)
				this.totalDevices = response.data.count
				this.devices = response.data.results

				const maxPages = Math.ceil(this.totalDevices / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadDevices(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		async loadTemplates() {
			try {
				const params = {}

				if (this.searchParams.selectedTenant) {
					params.where = { selectedTenant: this.searchParams.selectedTenant }
				}

				const response = await deviceTemplatesApi.getAll(params)
				this.deviceTemplates = response.data.results
			} catch (error) {
				console.error('Error loading templates:', error)
			}
		},

		routeTo(routeName) {
			if (routeName === '/devicetemplates/new') {
				this.editingTemplate = {
					name: '',
					manufacturer: '',
					model: '',
					description: '',
					template: '',
					tenantId: ''
				}
				this.showTemplateEditor = true
			} else {
				router.push(routeName)
			}
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/devices`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = 'teleflow-devices.csv'
				link.click()
			} catch (error) {
				console.error(error)
			}
		},

		deleteDevice(device) {
			this.$swal({
				title: 'Delete Device',
				text: `Would you like to delete device '${device.name}'?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed) {
					devicesApi
						.delete(device.id)
						.then(() => this.reloadDevices())
						.catch(console.error)
				}
			})
		},

		editTemplate(template) {
			this.loading = true
			deviceTemplatesApi
				.getById(template.id)
				.then((response) => {
					this.editingTemplate = response.data

					if (!this.editingTemplate.template) {
						this.editingTemplate.template = ''
					}

					if (this.editingTemplate.tenant && this.editingTemplate.tenant.id) {
						this.editingTemplate.tenantId = this.editingTemplate.tenant.id
					} else {
						this.editingTemplate.tenantId = ''
					}

					this.showTemplateEditor = true

					this.$nextTick(() => {
						if (this.$refs.modalEditor) {
							this.$refs.modalEditor.setValue(this.editingTemplate.template)
						}
					})
				})
				.catch((error) => {
					console.error('Error loading template:', error)
					this.$swal({
						title: 'Error',
						text: 'Failed to load template',
						icon: 'error',
						confirmButtonText: 'OK'
					})
				})
				.finally(() => {
					this.loading = false
				})
		},

		deleteTemplate(template) {
			this.$swal({
				title: 'Delete Template',
				text: `Would you like to delete template '${template.name}'?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed) {
					deviceTemplatesApi
						.delete(template.id)
						.then(() => {
							this.deviceTemplates = this.deviceTemplates.filter((t) => t.id !== template.id)
							this.$swal({
								title: 'Success',
								text: 'Template deleted successfully',
								icon: 'success',
								confirmButtonText: 'OK'
							})
						})
						.catch(console.error)
				}
			})
		},

		async saveTemplate() {
			try {
				if (!this.editingTemplate.name) {
					this.$swal({
						title: 'Error',
						text: 'Template name is required',
						icon: 'error',
						confirmButtonText: 'OK'
					})
					return
				}

				if (!this.editingTemplate.tenantId) {
					this.$swal({
						title: 'Error',
						text: 'Tenant selection is required',
						icon: 'error',
						confirmButtonText: 'OK'
					})
					return
				}

				this.loading = true

				const templateData = { ...this.editingTemplate }

				templateData.tenant = {
					id: parseInt(templateData.tenantId)
				}

				delete templateData.tenantId

				if (!templateData.template) {
					templateData.template = ''
				}

				let response

				if (templateData.id) {
					response = await deviceTemplatesApi.update(templateData)
					const index = this.deviceTemplates.findIndex((t) => t.id === templateData.id)
					if (index !== -1) {
						this.deviceTemplates[index] = { ...response }
					}
				} else {
					response = await deviceTemplatesApi.create(templateData)
					this.deviceTemplates.push(response)
				}

				this.showTemplateEditor = false
				this.$swal({
					title: 'Success',
					text: `Template ${templateData.id ? 'updated' : 'created'} successfully`,
					icon: 'success',
					confirmButtonText: 'OK'
				})

				this.loadTemplates()
			} catch (error) {
				console.error('Error saving template:', error)
				this.$swal({
					title: 'Error',
					text: 'Failed to save template',
					icon: 'error',
					confirmButtonText: 'OK'
				})
			} finally {
				this.loading = false
			}
		}
	}
}
</script>
